<template>
  <TemplateHead :config="config" />

  <div
    :class="fonts[config.style.font]"
    class="bg-gray-50 min-h-screen"
  >
    <div v-if="activeStepIndex !== 2">
      <div class="grid grid-cols-1 xl:grid-cols-2">
        <div class="py-8 md:py-16 px-6 md:px-12 lg:px-16 2xl:pr-32 2xl:pl-[20rem]">
          <img
            v-if="config.header?.logo_url"
            class="h-24 w-32 object-contain"
            :src="config.header?.logo_url"
            alt="Logo"
          >
          <img
            v-else-if="isPreview()"
            class="h-24 w-32 object-contain"
            src="https://placehold.co/160x50"
            alt="Logo"
          >

          <div class="pt-8 md:py-16 grid space-y-8">
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="text-4xl md:text-6xl font-bold uppercase"
              :style="{
                backgroundImage: `linear-gradient(to right, #${secondaryColor}, #${primaryColor})`,
                backgroundClip: 'text',
                color: 'transparent'
              }"
              v-html="config.header?.text"
            />

            <div
              class="text-xl"
              :style="{
                backgroundImage: `linear-gradient(to right, #${secondaryColor}, #${primaryColor})`,
                backgroundClip: 'text',
                color: 'transparent'
              }"
              v-html="config.header?.subtext"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>

        <div class="flex justify-center relative">
          <div
            class="absolute right-0 h-[35rem] w-full rounded-bl-3xl"
            :style="headerBackground"
          />

          <div class="px-8 pt-8 pb-16 lg:px-32 xl:pt-32 xl:pb-0 z-1 w-full flex justify-center md:justify-start">
            <div
              id="step"
              class="w-full bg-white rounded-3xl shadow-xl h-full max-w-[32rem]"
            >
              <div class="p-8 grid space-y-6 md:space-y-8">
                <Component
                  :is="activeStepComponent()"
                  v-if="activeStepComponent()"
                  :config="config"
                  :quiz-config="quizConfig"
                  :variables="variables"
                  :campaign-subs="exitCampaignSubs"
                  :can-submit="canSubmit"
                  @submit="submitStep()"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-6 md:grid-rows-2 2xl:px-64 gap-y-16">
        <div class="sm:col-span-6 md:col-span-5 lg:col-span-3 2xl:col-span-3">
          <div class="px-12 sm:px-16 pb-12 grid gap-y-8 2xl:pr-32 pt-4">
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="text-5xl font-bold"
              v-html="config.body?.text"
            />

            <div
              class="text-xl text-gray-600"
              v-html="config.body?.subtext"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>

        <div class="md:col-span-2 md:row-start-2">
          <div class="flex justify-center">
            <div class="w-20 md:w-24 h-20 md:h-24">
              <GiftIcon
                :primary-color="primaryColor"
                :secondary-color="secondaryColor"
              />
            </div>
          </div>

          <!-- eslint-disable vue/no-v-html -->
          <div
            class="w-full text-center py-8 px-16 text-xl text-gray-600"
            v-html="config.body?.gift_text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <div class="md:col-span-2 md:row-start-2">
          <div class="flex justify-center">
            <div class="w-20 md:w-24 h-20 md:h-24">
              <StatsIcon
                :primary-color="primaryColor"
                :secondary-color="secondaryColor"
              />
            </div>
          </div>

          <!-- eslint-disable vue/no-v-html -->
          <div
            class="w-full text-center py-8 px-16 text-xl text-gray-600"
            v-html="config.body?.poll_text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <div class="md:col-span-2 md:row-start-2">
          <div class="flex justify-center">
            <div
              class="w-20 md:w-24 h-20 md:h-24"
            >
              <ChatIcon
                :primary-color="primaryColor"
                :secondary-color="secondaryColor"
              />
            </div>
          </div>

          <!-- eslint-disable vue/no-v-html -->
          <div
            class="w-full text-center py-8 px-16 text-xl text-gray-600"
            v-html="config.body?.chat_text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>

      <div class="grid gap-y-8">
        <p class="text-center text-5xl font-bold">
          {{ __('co_reg_lander_001_content_review_title') }}
        </p>

        <!-- eslint-disable vue/no-v-html -->
        <div
          class="text-center text-3xl text-gray-500"
          v-html="__('co_reg_lander_001_content_review_intro')"
        />
        <!-- eslint-enable vue/no-v-html -->

        <div class="w-full grid grid-cols-1 xl:grid-cols-2 px-16 xl:px-[16rem] 2xl:px-[26rem] gap-x-16 py-16 gap-y-16">
          <div
            v-for="(experience, index) in config.body?.experiences ?? []"
            :key="index"
            class="relative shadow-md w-full px-12 pt-12 pb-8 rounded-xl bg-white"
          >
            <div class="absolute left-12 top-[-2rem]">
              <QuoteIcon
                :primary-color="primaryColor"
                :secondary-color="secondaryColor"
              />
            </div>

            <div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="text-gray-600 italic text-lg"
                v-html="experience.experience"
              />
              <!-- eslint-enable vue/no-v-html -->

              <div>
                <p class="py-2 text-lg font-semibold">
                  {{ experience.name }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-center">
          <div
            class="w-[32rem cursor-pointer p-4 rounded-md"
            :style="{
              backgroundColor: `#${primaryColor}`,
            }"
            @click="scrollToTop()"
          >
            <p class="text-center text-white text-xl font-bold">
              {{ __('co_reg_lander_001_content_review_button') }}
            </p>
          </div>
        </div>
      </div>

      <div class="w-full pt-32">
        <div
          class="h-[30rem]"
          :style="footerBackground"
        >
          <div class="flex justify-center grid grid-rows-3 h-full py-16">
            <p class="my-auto text-center font-bold text-white text-6xl">
              {{ __('co_reg_lander_001_content_join_title') }}
            </p>

            <p class="my-auto text-center font-semibold text-white text-2xl">
              {{ __('co_reg_lander_001_content_join_intro') }}
            </p>

            <div class="flex justify-center my-auto">
              <div
                class="w-[18rem] h-[4rem] border-2 border-white cursor-pointer p-4 rounded-md"
                @click="scrollToTop()"
              >
                <p class="text-center text-white text-xl font-bold">
                  {{ __('co_reg_lander_001_content_join_button') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="activeStepIndex === 2">
      <CoRegForm
        :loading="isLoadingOfferWall"
        :config="config"
        :campaign-subs="coRegCampaignSubs"
        :can-submit="canSubmit"
        @submit="submitStep()"
      />
    </div>

    <Footer
      v-if="activeStepIndex !== 2"
      :config="config"
      :pages="pages"
      :collector-flow-uuid="collectorFlowUuid"
    />
  </div>
</template>

<script setup>
import {defineProps, nextTick, onMounted, onUnmounted, ref} from 'vue';
import ChatIcon from '@timon/js/Templates/CO-REG-LANDER-001/Components/ChatIcon.vue';
import StatsIcon from '@timon/js/Templates/CO-REG-LANDER-001/Components/StatsIcon.vue';
import GiftIcon from '@timon/js/Templates/CO-REG-LANDER-001/Components/GiftIcon.vue';
import QuoteIcon from '@timon/js/Templates/CO-REG-LANDER-001/Components/QuoteIcon.vue';
import EmailForm from '@timon/js/Templates/CO-REG-LANDER-001/Components/EmailForm.vue';
import NameForm from '@timon/js/Templates/CO-REG-LANDER-001/Components/NameForm.vue';
import GenderBirthForm from '@timon/js/Templates/CO-REG-LANDER-001/Components/GenderBirthForm.vue';
import Footer from '@timon/js/Templates/CO-REG-LANDER-001/Components/Footer.vue';
import CoRegForm from '@timon/js/Templates/CO-REG-LANDER-001/Components/CoRegForm.vue';
import axios from 'axios';
import Exit from '@timon/js/Templates/CO-REG-LANDER-001/Components/Exit.vue';
import {
  checkIfTargetingCookiesAreAccepted,
  fingerprint,
  isPreview,
  registerVisit,
} from '@timon/js/Mixins/helpers';
import {useStore} from 'vuex';
import TemplateHead from '@timon/js/Components/TemplateHead.vue';
import {pushDataLayer} from '@global/js/Mixins/helpers';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  quizConfig: {
    type: Object,
    required: true,
  },

  variables: {
    type: Object,
    required: true,
  },

  pages: {
    type: Array,
    required: true,
  },

  collectorFlowUuid: {
    type: String,
    required: true,
  },

  adUrlUuid: {
    type: String,
    required: false,
    default: null,
  },

  campaignSubId: {
    type: [Number, null],
    required: false,
    default: null,
  },

  fingerprintRequired: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const fonts = {
  'mono': 'font-mono',
  'sans': 'font-sans',
  'serif': 'font-serif',
};

const activeStepIndex = ref(0);

const form = {
  gender: null,
  birth_date: null,
  email: null,
  selected_co_reg_campaign_subs: [],
  first_name: null,
  last_name: null,
  postcode: null,
  terms_accepted: false,
};

const primaryColor = ref(props.config.style?.primary);
const secondaryColor = ref(props.config.style?.secondary);

const isMobile = ref(false);

const headerBackground = ref(null);
const footerBackground = ref(null);

const coRegCampaignSubs = ref([]);
const exitCampaignSubs = ref([]);

const landingParticipantUuid = ref(null);
const landingVisitUuid = ref(null);
const isLoadingOfferWall = ref(false);

const fingerprintId = ref(null);
const canSubmit = ref(true);

const store = useStore();

store.commit('errors/setErrors', []);
store.commit('form/setForm', form);

function updateIsMobile() {
  isMobile.value = window.innerWidth < 768;
}

function acceptedCookies() {
  checkIfTargetingCookiesAreAccepted(() => registerVisit(props.adUrlUuid));
}

onMounted(() => {
  updateIsMobile();

  window.addEventListener('resize', updateIsMobile);
  window.addEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.addEventListener('CookieScriptAccept', acceptedCookies);

  const footerBackgroundUrl = props.config.footer?.background_url ?? new URL('@timon/js/Templates/CO-REG-LANDER-001/assets/footer-background.jpg', import.meta.url).href;
  footerBackground.value = {
    background: `#000 url(${footerBackgroundUrl}) center center no-repeat`,
    backgroundSize: 'cover',
  };

  const headerBackgroundUrl = props.config.header?.background_url ?? new URL('@timon/js/Templates/CO-REG-LANDER-001/assets/header-background.jpg', import.meta.url).href;
  headerBackground.value = {
    background: `#000 url(${headerBackgroundUrl}) top right no-repeat`,
  };

  if (!isPreview()) {
    registerVisit(props.adUrlUuid, (data) => {
      landingVisitUuid.value = data.visit_key;
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
  window.removeEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.removeEventListener('CookieScriptAccept', acceptedCookies);
});

function nextStep() {
  activeStepIndex.value += 1;

  nextTick(() => {
    scrollToStep();
  });

  pushDataLayer({
    event: 'next_step',
    step: activeStepIndex.value,
  });
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

function scrollToStep() {
  const stepElement = document.getElementById('step');
  const subStepElement = document.getElementById('sub-step');

  if (subStepElement) {
    subStepElement.scrollIntoView({
      behavior: 'smooth',
    });
  } else if (stepElement) {
    stepElement.scrollIntoView({
      behavior: 'smooth',
    });
  } else {
    scrollToTop();
  }
}

function activeStepComponent() {
  switch (activeStepIndex.value) {
    case 0:
      return GenderBirthForm;
    case 1:
      return EmailForm;
    case 3:
      return NameForm;
    case 4:
      return Exit;
    default:
      return null;
  }
}

async function submitStep() {
  canSubmit.value = false;
  store.commit('errors/clearErrors');
  isLoadingOfferWall.value = true;

  let isFinalStep = false;
  let includeOfferWall = false;
  let includeExitWall = false;

  if (props.fingerprintRequired && activeStepIndex.value === 0) {
    if (!isPreview() && !fingerprintId.value) {
      try {
        const fp = await fingerprint();
        const result = await fp.get();
        fingerprintId.value = result.visitorId;
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (activeStepIndex.value === 1) {
    includeOfferWall = true;
  }

  if (activeStepIndex.value === 3) {
    includeExitWall = true;
    isFinalStep = true;
  }

  if (isPreview()) {
    axios.post(route('zazu.v1.landing.form.submit-preview', {
      campaignSub: props.campaignSubId,
    }), {
      include_offer_wall: includeOfferWall,
      include_exit_wall: includeExitWall,
    })
      .then((response) => {
        canSubmit.value = true;
        if (!!response.data.offer_wall && response.data.offer_wall.length > 0) {
          coRegCampaignSubs.value = response.data.offer_wall;
        }
        exitCampaignSubs.value = response.data.exit_wall;

        isLoadingOfferWall.value = false;

        nextStep();
      });
  }

  axios.post(route(isFinalStep ? 'zazu.v1.landing.form.submit' : 'zazu.v1.landing.form.validate', {
    adUrl: props.adUrlUuid,
    step: activeStepIndex.value,
  }), {
    is_final_step: isFinalStep,
    include_offer_wall: includeOfferWall,
    include_exit_wall: includeExitWall,
    landing_visit_uuid: landingVisitUuid.value,
    collectorFlow: props.collectorFlowUuid,
    fingerprint: fingerprintId.value,
    selected: store.state.form.selected_co_reg_campaign_subs,

    ...store.state.form,
  })
    .then((response) => {
      if (!!response.data.offer_wall && response.data.offer_wall.length > 0) {
        coRegCampaignSubs.value = response.data.offer_wall;
      }
      exitCampaignSubs.value = response.data.exit_wall;
      landingParticipantUuid.value = response.data.landing_participant_uuid;

      isLoadingOfferWall.value = false;

      nextStep();

      canSubmit.value = true;
    })
    .catch((error) => {
      if (error.response.data.errors) {
        store.commit('errors/setErrors', error.response.data.errors);
      }

      canSubmit.value = true;
    });
}
</script>
